import { forwardRef, InputHTMLAttributes, ReactNode, useId } from 'react'
import { cn } from 'utils/cn'

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
    label?: string
    helperText?: string
    error?: string
    icon?: ReactNode
    iconRight?: ReactNode
    required?: boolean
    /**
     * Block browser / password-manager autofill. Chrome was pre-filling driver and
     * contractor contact fields (email, SSN, phone) with the signed-in dispatcher's
     * own Numeo login, which then either saved the wrong data or collided with the
     * backend email-uniqueness check ("Email already in use by another user").
     * Applies a non-semantic name + autoComplete + LastPass/1Password opt-out so
     * heuristic autofill has nothing to match on.
     */
    noAutofill?: boolean
}

export const inputClasses =
    'w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 shadow-sm placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/20 disabled:opacity-50 disabled:cursor-not-allowed'

export const Input = forwardRef<HTMLInputElement, InputProps>(
    ({ label, helperText, error, icon, iconRight, required, className = '', noAutofill, name, autoComplete, ...props }, ref) => {
        const generatedId = useId()
        const autofillProps = noAutofill
            ? {
                  name: name ?? `nofill-${generatedId}`,
                  autoComplete: autoComplete ?? (props.type === 'password' ? 'new-password' : 'off'),
                  'data-lpignore': 'true',
                  'data-1p-ignore': 'true',
              }
            : { name, autoComplete }
        return (
            <div className="space-y-1.5">
                {label && (
                    <label className="text-sm font-medium text-slate-900 dark:text-slate-100">
                        {label}
                        {required && <span className="ml-0.5 text-red-500">*</span>}
                    </label>
                )}
                <div className="relative">
                    {icon && <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500">{icon}</div>}
                    <input
                        ref={ref}
                        className={cn(
                            inputClasses,
                            icon && 'pl-9',
                            iconRight && 'pr-9',
                            error && 'border-red-500 focus:border-red-500 focus:ring-red-500/20',
                            // Last so a caller's size/padding override actually wins: `inputClasses`
                            // hardcodes `px-3 py-2 text-sm`, and tailwind-merge resolves the conflict
                            // by last-wins instead of emitting both for the cascade to arbitrate.
                            className
                        )}
                        {...autofillProps}
                        {...props}
                    />
                    {iconRight && <div className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500">{iconRight}</div>}
                </div>
                {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
                {helperText && !error && <p className="text-xs text-slate-500 dark:text-slate-400">{helperText}</p>}
            </div>
        )
    }
)

Input.displayName = 'Input'
